import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useStockMonitoringRealtime = () => {
  const queryClient = useQueryClient();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedInvalidateAll = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['stock-status'] });
    }, 1000);
  }, [queryClient]);

  useEffect(() => {
    const monitoringChannel = supabase
      .channel('stock-monitoring-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notification_product_monitoring' },
        (payload) => {
          const data = payload.new || payload.old;
          if (data && 'notification_id' in data && data.notification_id) {
            queryClient.invalidateQueries({ 
              queryKey: ['stock-status', data.notification_id] 
            });
          }
        }
      )
      .subscribe();

    const inventoryChannel = supabase
      .channel('inventory-checks-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notification_inventory_checks' },
        () => {
          // Debounced invalidation instead of per-row lookup
          debouncedInvalidateAll();
        }
      )
      .subscribe();

    // Debounced invalidation for voorraad_live changes (prevents request storm)
    const voorraadChannel = supabase
      .channel('voorraad-stock-monitoring')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'voorraad_live' },
        () => {
          debouncedInvalidateAll();
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      supabase.removeChannel(monitoringChannel);
      supabase.removeChannel(inventoryChannel);
      supabase.removeChannel(voorraadChannel);
    };
  }, [queryClient, debouncedInvalidateAll]);
};
